import { useQuery } from '@tanstack/react-query'
import { artistService, releaseService } from '../services/apiService'
import type { Artist } from '../types'

function ArtistCard({ artist }: { artist: Artist }) {
  const { data: releases, isLoading: releasesLoading } = useQuery({
    queryKey: ['releases', 'artist', artist.id],
    queryFn: () => releaseService.getByArtist(artist.id),
  })

  return (
    <div className="border rounded-lg p-6 bg-white shadow-sm">
      {artist.imageUrl && (
        <img
          src={artist.imageUrl}
          alt={artist.name}
          className="w-full h-48 object-cover rounded mb-4"
        />
      )}
      <h2 className="text-2xl font-bold mb-2">{artist.name}</h2>
      <p className="text-gray-600 mb-4">{artist.bio}</p>

      {/* Social Links */}
      {(artist.spotifyUrl || artist.instagramUrl || artist.facebookUrl) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {artist.spotifyUrl && (
            <a
              href={artist.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-green-600 hover:text-green-800 hover:underline"
            >
              Spotify
            </a>
          )}
          {artist.instagramUrl && (
            <a
              href={artist.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-pink-600 hover:text-pink-800 hover:underline"
            >
              Instagram
            </a>
          )}
          {artist.facebookUrl && (
            <a
              href={artist.facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Facebook
            </a>
          )}
        </div>
      )}

      {/* Releases Section */}
      <div className="mt-4 border-t pt-4">
        <h3 className="text-lg font-semibold mb-3">Releases</h3>
        {releasesLoading ? (
          <p className="text-sm text-gray-500">Loading releases...</p>
        ) : releases && releases.length > 0 ? (
          <div className="space-y-2">
            {releases.map((release) => (
              <div key={release.id} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                {release.coverArtUrl && (
                  <img
                    src={release.coverArtUrl}
                    alt={release.title}
                    className="w-16 h-16 object-cover rounded flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{release.title}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(release.releaseDate).getFullYear()} • {release.type.toUpperCase()}
                  </p>
                  {release.streamingLinks?.bandcamp && (
                    <a
                      href={release.streamingLinks.bandcamp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      Listen on Bandcamp →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No releases yet</p>
        )}
      </div>
    </div>
  )
}

export default function ArtistsPage() {
  const { data: artists, isLoading } = useQuery({
    queryKey: ['artists'],
    queryFn: artistService.getAll,
  })

  if (isLoading) return <div>Loading artists...</div>

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Our Artists</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {artists?.map((artist) => (
          <ArtistCard key={artist.id} artist={artist} />
        ))}
      </div>
    </div>
  )
}
